/**
 * CoryMessageContent — renders a Cory assistant message and auto-injects
 * inline <SourceBadge /> chips wherever a citation marker appears.
 *
 * Two marker syntaxes supported:
 *   [source:abs_8165]         → strong (explicit machine-readable)
 *   (ABS 8165.0)              → soft (common natural-language pattern)
 *
 * Soft patterns map human-friendly strings → canonical source IDs via
 * CITATION_MAP. Unmapped markers render as plain text unchanged.
 */
import React from 'react';
import { SourceBadge } from './SourceBadge';

// Canonical source IDs registered in /app/backend/config/benchmark_sources.py.
// When Cory's natural-language response mentions any of these phrases, we
// inject the corresponding SourceBadge inline.
const CITATION_MAP = [
  // Order matters — longer matches first
  { pattern: /ABS\s*81?65(?:\.0)?/gi,         id: 'abs_8165' },
  { pattern: /ABS\s*8501(?:\.0)?/gi,          id: 'abs_8501' },
  { pattern: /ATO\s*(?:small\s*business\s*)?benchmarks?/gi, id: 'ato_benchmarks' },
  { pattern: /RBA\s*F1\.?1/gi,                id: 'rba_f11' },
  { pattern: /RBA\s*I2/gi,                    id: 'rba_i2' },
  { pattern: /AEMO\s*(?:NEM)?/gi,             id: 'aemo_nem' },
  { pattern: /Medicare\s*MBS/gi,              id: 'medicare_mbs' },
  { pattern: /Fair\s*Work/gi,                 id: 'fair_work' },
  { pattern: /Corvana\s*(?:customer\s*)?aggregate/gi, id: 'corvana_customer_aggregate' },
];

// Strong marker: [source:xxx]
const STRONG_MARKER = /\[source:([a-z0-9_]+)\]/gi;

/**
 * Split the message into an array of {type: 'text'|'badge', ...} segments.
 * Strong markers take precedence; soft-pattern injection only applies to
 * residual text segments.
 */
function segmentMessage(content) {
  if (typeof content !== 'string' || !content) return [{ type: 'text', text: '' }];

  const segments = [];
  let lastIdx = 0;
  for (const match of content.matchAll(STRONG_MARKER)) {
    if (match.index > lastIdx) {
      segments.push({ type: 'text', text: content.slice(lastIdx, match.index) });
    }
    segments.push({ type: 'badge', id: match[1].toLowerCase() });
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < content.length) {
    segments.push({ type: 'text', text: content.slice(lastIdx) });
  }

  // For each text segment, apply soft-citation substitution
  const out = [];
  for (const seg of segments) {
    if (seg.type !== 'text') {
      out.push(seg);
      continue;
    }
    let pieces = [{ type: 'text', text: seg.text }];
    for (const { pattern, id } of CITATION_MAP) {
      const next = [];
      for (const piece of pieces) {
        if (piece.type !== 'text') { next.push(piece); continue; }
        let cursor = 0;
        const str = piece.text;
        for (const m of str.matchAll(pattern)) {
          if (m.index > cursor) next.push({ type: 'text', text: str.slice(cursor, m.index) });
          next.push({ type: 'inlineCitation', text: m[0], id });
          cursor = m.index + m[0].length;
        }
        if (cursor < str.length) next.push({ type: 'text', text: str.slice(cursor) });
      }
      pieces = next;
    }
    out.push(...pieces);
  }
  return out;
}

export function CoryMessageContent({ content, sources }) {
  const segments = React.useMemo(() => segmentMessage(content), [content]);

  // Explicit sources list takes priority over inline detection. If the API
  // returned `sources: ['abs_8165', ...]` we render them all as a footer row.
  const explicitSources = Array.isArray(sources)
    ? sources.filter((id) => typeof id === 'string' && /^[a-z0-9_]+$/.test(id))
    : [];

  return (
    <>
      <span className="whitespace-pre-wrap">
        {segments.map((seg, i) => {
          if (seg.type === 'text') return <React.Fragment key={i}>{seg.text}</React.Fragment>;
          if (seg.type === 'badge') {
            return (
              <span key={i} className="inline-block align-middle mx-0.5">
                <SourceBadge sourceId={seg.id} compact />
              </span>
            );
          }
          // inlineCitation — render original text + subtle SourceBadge suffix
          return (
            <React.Fragment key={i}>
              {seg.text}
              <span className="inline-block align-middle ml-1">
                <SourceBadge sourceId={seg.id} compact />
              </span>
            </React.Fragment>
          );
        })}
      </span>
      {explicitSources.length > 0 && (
        <div className="mt-2.5 pt-2 border-t border-slate-200/60" data-testid="cory-sources">
          <p className="text-[10px] text-slate-400 mb-1.5">Sources:</p>
          <div className="flex flex-wrap gap-1.5">
            {explicitSources.map((id) => (
              <SourceBadge key={id} sourceId={id} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default CoryMessageContent;
